import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllGroupChats,
  getChatById,
  createGroupChat as apiCreateGroupChat,
  editChatMessage as apiEditChatMessage,
  deleteChatMessage as apiDeleteChatMessage,
  sendMessage,
} from "../../helpers/apis";

// ✅ Get all group chats with pagination
export const fetchAllGroupChats = createAsyncThunk(
  "chats/fetchAllGroupChats",
  async (params = {}, { rejectWithValue, getState }) => {
    const { search = '', page = 1, limit = 50 } = params;
    try {
      const response = await getAllGroupChats({ search, type: 'group', page, limit });

      if (response && response.status === 1) {
        const data = response.data;
        let chats = [];
        let hasMore = false;
        let totalData = response.totalData || 0;

        if (Array.isArray(data)) {
          chats = data;
          hasMore = data.length >= limit;
        } else if (data?.data && Array.isArray(data.data)) {
          chats = data.data;
          hasMore = data.hasMore !== undefined ? data.hasMore : (data.data.length >= limit);
          if (data.totalData) totalData = data.totalData;
        }

        return { chats, page, hasMore, totalData };
      }

      return { chats: [], page, hasMore: false, totalData: 0 };
    } catch (err) {
      if (err.message && (err.message.includes("not found") || err.message.includes("No groups"))) {
        return { chats: [], page, hasMore: false, totalData: 0 };
      }
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.data?.message ||
        err.message ||
        "Failed to fetch group chats";
      return rejectWithValue(errorMessage);
    }
  },
  {
    condition: (params, { getState }) => {
      const state = getState();
      const { loading } = state.Chat || {};
      if (loading) return false;
      return true;
    }
  }
);

// ✅ Fetch chat by ID with dynamic limit (no page needed)
export const fetchChatById = createAsyncThunk(
  "Chat/fetchChatById",
  async (params, { rejectWithValue, getState }) => {
    const chatId = typeof params === 'string' ? params : params.chatId;
    const limit = typeof params === 'object' ? params.limit || 50 : 50;

    if (!chatId) return rejectWithValue("Chat ID is missing");

    try {
      const response = await getChatById(chatId, { limit });

      if (!response) {
        return rejectWithValue("No response from server");
      }

      if (response.status === 0) {
        return rejectWithValue(response.message || "Chat not found or not accepted");
      }

      if (response.status !== 1) {
        return rejectWithValue(response.message || "Failed to fetch chat");
      }

      const apiData = response.data;
      if (!apiData) {
        return rejectWithValue("No chat data returned from API");
      }

      const groupChatId = apiData.chatRequestId || apiData._id || apiData.groupId || chatId;
      const state = getState();
      const groupChats = state.Chat?.groupChats || [];

      let groupMetadata = null;
      if (groupChats.length > 0) {
        groupMetadata = groupChats.find(g => {
          const gId = String(g._id || g.chatRequestId || g.groupId || '');
          const searchId = String(groupChatId || chatId || '');
          return gId === searchId ||
            String(g._id) === String(chatId) ||
            String(g.chatRequestId) === String(chatId) ||
            String(g.groupId) === String(chatId);
        });
      }

      if (!groupMetadata) {
        try {
          const { getAllGroupChats } = await import("../../helpers/apis");
          const groupResponse = await getAllGroupChats();
          if (groupResponse.status === 1 && Array.isArray(groupResponse.data)) {
            groupMetadata = groupResponse.data.find(
              g => String(g._id) === String(groupChatId) ||
                String(g.chatRequestId) === String(groupChatId) ||
                String(g.groupId) === String(groupChatId)
            );
          }
        } catch (groupErr) {
          console.warn("Could not fetch group metadata:", groupErr);
        }
      }

      const messages = Array.isArray(apiData.messages)
        ? apiData.messages.map(msg => ({
          _id: msg._id || msg.messageId,
          content: msg.content,
          createdAt: msg.createdAt,
          sender: msg.sender || { _id: "unknown", firstname: "Unknown" },
          type: msg.type || (msg.sender?._id ? "receive" : "send"),
          time: msg.time,
          messageType: msg.messageType,
          mediaUrl: msg.mediaUrl,
          fileName: msg.fileName,
          isEdited: Boolean(msg.isEdited), // Ensure this is included
          editedAt: msg.editedAt || null, // Ensure this is included
          canEdit: msg.canEdit !== undefined ? msg.canEdit : true // Ensure this is included
        }))
        : [];

      const totalData = response.totalData || apiData.totalData || messages.length;

      const mergedData = {
        _id: groupChatId,
        chatRequestId: groupChatId,
        groupId: groupChatId,
        name: groupMetadata?.name || apiData.name || "Chat",
        groupImage: apiData.groupImage || groupMetadata?.groupImage || null,
        messages,
        members: (groupMetadata?.members && groupMetadata.members.length > 0) ? groupMetadata.members : (apiData.members || []),
        groupAdmin: groupMetadata?.groupAdmin || apiData.groupAdmin || null,
        superAdmins: (groupMetadata?.superAdmins && groupMetadata.superAdmins.length > 0) ? groupMetadata.superAdmins : (Array.isArray(apiData.superAdmins) ? apiData.superAdmins : []),
        counts: groupMetadata?.counts || apiData.counts || (groupMetadata?.membersCount ? { membersCount: groupMetadata.membersCount } : null),
        totalData,
        currentLimit: limit,
      };

      return mergedData;
    } catch (err) {
      console.error("Error fetching chat:", err);
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.data?.message ||
        err.message ||
        "Network error";
      return rejectWithValue(errorMessage);
    }
  },
  {
    condition: (chatId, { getState }) => {
      if (!chatId) return false;
      const state = getState();
      const { loading, currentChat } = state.Chat || {};
      const currentChatId = currentChat?._id || currentChat?.chatRequestId || currentChat?.groupId;

      if (loading && currentChatId && String(currentChatId) === String(chatId)) {
        return false;
      }

      return true;
    }
  }
);

// ✅ Load more messages with increased limit (single API call)
// ✅ FIXED: Load more messages with increased limit
export const loadMoreMessagesWithLimit = createAsyncThunk(
  "Chat/loadMoreMessagesWithLimit",
  async ({ chatId, limit }, { rejectWithValue }) => {
    if (!chatId) return rejectWithValue("Chat ID is missing");

    console.log("🚀 API Call: Loading messages with limit", { chatId, limit });

    try {
      const response = await getChatById(chatId, { limit });

      if (!response || response.status !== 1) {
        console.error("❌ API Error:", response);
        return rejectWithValue("Failed to load more messages");
      }

      const apiData = response.data;
      const messages = Array.isArray(apiData.messages)
        ? apiData.messages.map(msg => ({
          _id: msg._id || msg.messageId,
          content: msg.content,
          createdAt: msg.createdAt,
          sender: msg.sender || { _id: "unknown", firstname: "Unknown" },
          type: msg.type || (msg.sender?._id ? "receive" : "send"),
          time: msg.time,
          messageType: msg.messageType,
          mediaUrl: msg.mediaUrl,
          fileName: msg.fileName,
          isEdited: Boolean(msg.isEdited),
          editedAt: msg.editedAt || null,
        }))
        : [];

      const totalData = response.totalData || apiData.totalData || messages.length;

      console.log("✅ API Success:", {
        messagesLoaded: messages.length,
        totalData,
        requestedLimit: limit
      });

      return { messages, totalData, currentLimit: limit };
    } catch (err) {
      console.error("❌ API Exception:", err);
      const errorMessage = err.response?.data?.message || err.message || "Failed to load more messages";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Create a new group chat
export const createGroupChat = createAsyncThunk(
  "chats/createGroupChat",
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiCreateGroupChat(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ✅ Send a message
// ✅ Send a message - FIXED VERSION
export const sendChatMessage = createAsyncThunk(
  "Chat/sendChatMessage",
  async ({ chatId, content }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await sendMessage(chatId, { message: content });

      const serverMsg = response.data?.data || null;
      if (!serverMsg) return serverMsg;

      if (serverMsg.message && serverMsg.content === undefined) {
        serverMsg.content = serverMsg.message;
      }

      // ✅ Immediately dispatch socket event after successful send
      setTimeout(() => {
        try {
          const { socket } = require("../../common/socket");
          const state = getState();
          const currentUserId = state.Chat?.currentUserId ||
            JSON.parse(localStorage.getItem("authUser"))?.user?._id ||
            JSON.parse(localStorage.getItem("authUser"))?._id;

          if (socket && currentUserId) {
            console.log("📤 [THUNK] Emitting sendMessage via socket:", { chatId, content, currentUserId });
            socket.emit("sendMessage", {
              chatId: chatId,
              message: content,
              senderId: currentUserId
            });
          }
        } catch (error) {
          console.error("❌ Error emitting socket event:", error);
        }
      }, 100);

      return serverMsg;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Add this to your thunk file
export const fetchMoreMessages = createAsyncThunk(
  "Chat/fetchMoreMessages",
  async ({ chatId, page = 1, limit = 20 }, { rejectWithValue }) => {
    if (!chatId) return rejectWithValue("Chat ID is missing");

    try {
      console.log("🚀 Loading more messages - Page:", page, "Limit:", limit);

      const response = await getChatById(chatId, { page, limit });

      if (!response) {
        console.error("❌ No response from server");
        return rejectWithValue("No response from server");
      }

      if (response.status !== 1) {
        console.error("❌ API Error:", response.message);
        return rejectWithValue(response.message || "Failed to load more messages");
      }

      const apiData = response.data;
      console.log("📄 API Response Data:", apiData);

      const messages = Array.isArray(apiData.messages)
        ? apiData.messages.map(msg => ({
          _id: msg._id || msg.messageId,
          content: msg.content,
          createdAt: msg.createdAt,
          sender: msg.sender || { _id: "unknown", firstname: "Unknown" },
          type: msg.type || (msg.sender?._id ? "receive" : "send"),
          time: msg.time,
          messageType: msg.messageType,
          mediaUrl: msg.mediaUrl,
          fileName: msg.fileName,
          isEdited: Boolean(msg.isEdited),
          editedAt: msg.editedAt || null,
        }))
        : [];

      const totalData = response.totalData || apiData.totalData || messages.length;
      const hasMore = messages.length >= limit;

      console.log("✅ Loaded messages:", messages.length, "Has more:", hasMore);

      return {
        messages,
        totalData,
        page,
        hasMore,
        chatId
      };
    } catch (err) {
      console.error("❌ API Exception:", err);
      const errorMessage = err.response?.data?.message || err.message || "Failed to load more messages";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Edit a chat message
export const editChatMessage = createAsyncThunk(
  "Chat/editChatMessage",
  async ({ chatId, messageId, content }, { rejectWithValue }) => {
    try {
      const response = await apiEditChatMessage(chatId, messageId, { content });
      if (response?.status !== 1) {
        return rejectWithValue(response?.message || "Failed to edit message");
      }

      // Ensure the response includes edit metadata
      const updatedMessage = {
        ...response.data,
        isEdited: true,
        editedAt: response.data.editedAt || new Date().toISOString()
      };

      return { chatId, messageId, updatedMessage };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Error editing message";
      return rejectWithValue(msg);
    }
  }
);

// ✅ Delete single or bulk messages
export const deleteChatMessage = createAsyncThunk(
  "Chat/deleteChatMessage",
  async ({ chatId, payload }, { rejectWithValue }) => {
    try {
      const response = await apiDeleteChatMessage(chatId, payload);
      if (response?.status !== 1) {
        return rejectWithValue(response?.message || "Failed to delete messages");
      }
      return { chatId, ...response.data };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Error deleting messages";
      return rejectWithValue(msg);
    }
  }
);

export const updateChatListOrder = (chatId) => ({
  type: 'chat/updateChatListOrder',
  payload: chatId
});